import { zodResolver } from '@hookform/resolvers/zod';
import { EnvelopeSimpleIcon, LockKeyIcon } from '@phosphor-icons/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { signIn as amplifySignIn } from 'aws-amplify/auth';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import z from 'zod';

import { Form } from '@components/form';
import { InputControlled } from '@components/input/controlled';
import { Button } from '@components/ui/button';
import { resolvePostSignInRedirect } from '@helpers/authRedirect.helper';
import { cn } from '@lib/utils';

const redirectUrlSchema = z.object({
  redirect: z.string().optional(),
});

export const Route = createFileRoute('/_auth-layout/login')({
  component: SignIn,
  validateSearch: search => redirectUrlSchema.parse(search),
});

const formSchema = z.object({
  email: z
    .email()
    .min(1, 'Email is required and must be valid (e.g. example@email.eu)'),
  password: z.string().min(1, 'Password is required'),
});

function SignIn() {
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const { redirect } = Route.useSearch();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const { isPending, mutate: signIn } = useMutation({
    mutationFn: amplifySignIn,
    onError: () => {
      toast.error('Warning', {
        description: 'Invalid credentials. Please try again.',
      });
    },
    onSuccess: async ({ isSignedIn }) => {
      if (!isSignedIn) {
        toast.error('Warning', {
          description: 'This account needs an extra sign-in step.',
        });
        return;
      }

      queryClient.clear();
      await navigate({
        href: resolvePostSignInRedirect(redirect),
        replace: true,
      });
    },
  });

  const handleSubmit = form.handleSubmit(values => {
    signIn({
      username: values.email,
      password: values.password,
    });
  });

  return (
    <Form.Provider {...form}>
      <form
        className={cn('mt-12 grid w-full gap-2 transition-all duration-200', {
          'space-y-4': !!form.formState.errors.email,
        })}
        onSubmit={handleSubmit}
      >
        <InputControlled
          icon={EnvelopeSimpleIcon}
          name="email"
          placeholder="example@email.eu"
          size="xl"
        />
        <InputControlled
          autoComplete="off"
          icon={LockKeyIcon}
          name="password"
          placeholder="******"
          secure
          size="xl"
          type="password"
        />

        <Button
          className="mx-auto mt-12 w-4/6"
          isLoading={isPending}
          size="lg"
          type="submit"
          variant="secondary"
        >
          Login
        </Button>
      </form>
    </Form.Provider>
  );
}
